import {
  Modal as CModal,
  ModalCloseButton as CModalCloseButton,
  ModalContent as CModalContent,
  ModalOverlay as CModalOverlay,
} from '@chakra-ui/modal';
import { ChevronLeftIcon, CloseIcon } from '@sketchnote/icons';
import classNames from 'classnames';
import * as React from 'react';

import { DialogProps } from '../dialogs/types';
import { MenuItem } from '../menu/menu.component';
import { CommonModalProps } from './modal.types';
import { Footer, Header, ModalWrapper } from './sub-components';

export const CommonModal = ({
  children,
  headText,
  footer,
  header,
  footerMainLabel,
  footerSecLabel,
  contentStyle,
  onClose,
  onMainClick,
  footerButtonStatus,
  footerButtonDisabled,
  onSecClick,
  isOpen,
  ...otherProps
}: CommonModalProps): JSX.Element => {
  return (
    <CModal isOpen={isOpen} onClose={onClose} {...otherProps}>
      <CModalOverlay />

      <CModalContent
        style={contentStyle}
        minWidth="auto"
        borderRadius="0.8rem"
        margin="auto"
        maxWidth="80rem"
      >
        <div className="bg-white rounded-lg px-32 mt-32">
          <CModalCloseButton className="w-12 outline-none" />
          {header ? header : <Header title={headText} />}
          <div className="mt-16 mb-24">{children}</div>
          {footer
            ? footer
            : (footerSecLabel || footerMainLabel) && (
                <Footer
                  mainLabel={footerMainLabel}
                  secLabel={footerSecLabel}
                  status={footerButtonStatus}
                  disabled={footerButtonDisabled}
                  onMainClick={onMainClick}
                  onSecClick={onSecClick}
                />
              )}
        </div>
      </CModalContent>
    </CModal>
  );
};

export interface CommonModalWithSidebarProps extends DialogProps {
  onCloseBtnClick?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
  onBackBtnClick?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
  isActive: (arg: string) => boolean;
  menuItems?: {
    sectionTitle?: string;
    items: { label: string | JSX.Element; value: string; icon?: JSX.Element }[];
  }[];
  headText?: string;
  handleMenuClick?: (value: string) => void;
  width?: string;
}

export const CommonModalWithSidebar = ({
  children,
  onCloseBtnClick,
  menuItems,
  headText,
  handleMenuClick,
  isActive,
  width = '784px',
  onBackBtnClick,
}: CommonModalWithSidebarProps): JSX.Element => {
  return (
    <div className="flex bg-white rounded-lg overflow-auto">
      <div style={{ width: '22.4rem' }} className="bg-gray-100 px-20 py-24 rounded-l-lg">
        <h3 className="font-medium leading-32 text-base mb-24">{headText}</h3>
        <nav className="flex flex-col">
          {menuItems?.map(({ sectionTitle, items }, index) => (
            <React.Fragment key={`${sectionTitle}-${index}`}>
              {sectionTitle && (
                <span className="text-xs mb-18 mt-16 font-medium text-gray-500">
                  {sectionTitle}
                </span>
              )}
              {items?.map(({ label, value }, index) => (
                <MenuItem
                  key={`${value}-${index}`}
                  className={classNames({
                    'text-sm mb-8 text-gray-600': true,
                    'text-primary-500 bg-primary-100 hover:bg-blue-100': isActive(value),
                  })}
                  onClick={handleMenuClick?.bind(this, value)}
                >
                  {label}
                </MenuItem>
              ))}
            </React.Fragment>
          ))}
        </nav>
      </div>

      <div className="bg-white border-r rounded-lg">
        <div className="flex justify-between h-48 items-center px-20">
          <button aria-label="Close icon" onClick={onBackBtnClick}>
            {onBackBtnClick && <ChevronLeftIcon className="text-gray-500 text-xs" />}
          </button>
          <button aria-label="Close icon" onClick={onCloseBtnClick}>
            <CloseIcon className="text-gray-500 text-xs" />
          </button>
        </div>
        <div style={{ width: `calc(${width} - 224px)` }}>{children}</div>
      </div>
    </div>
  );
};

const Modal = {
  Wrapper: ModalWrapper,
  Header,
  Footer,
};

export default Modal;
